import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import ChangePasswordModal from '../../components/account/ChangePasswordModal';
import { apiService } from '../../services/api';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });

  const [originalData, setOriginalData] = useState({});

  // Load profile data
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      // KAN-182: route through apiService (env base URL + HttpOnly auth cookie) instead of a hardcoded fetch.
      const data = await apiService.get('/storefront/profile');
      const profileData = data.data || data;

      const profile = {
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        email: profileData.email || '',
        phoneNumber: profileData.phoneNumber || ''
      };

      setFormData(profile);
      setOriginalData(profile);
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fall back to user context
      if (user) {
        const profile = {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || ''
        };
        setFormData(profile);
        setOriginalData(profile);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Phone formatting
    if (name === 'phoneNumber') {
      const formatted = formatPhone(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear validation error
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Clear messages
    if (errorMessage) setErrorMessage('');
    if (successMessage) setSuccessMessage('');
  };

  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 10);
    
    if (limited.length === 0) return '';
    if (limited.length <= 3) return `(${limited}`;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName?.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.length > 50) {
      errors.firstName = 'First name cannot exceed 50 characters';
    }

    if (!formData.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.length > 50) {
      errors.lastName = 'Last name cannot exceed 50 characters';
    }

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = 'Invalid email address';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // KAN-182: route through apiService (env base URL + HttpOnly auth cookie) instead of a hardcoded fetch.
      const data = await apiService.put('/storefront/profile', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber || null
      });

      if (data.status === 'Success' || data.Status === 'Success') {
        setSuccessMessage(data.message || data.Message || 'Profile updated successfully!');
        setOriginalData({ ...formData });
        setIsEditing(false);
        
        // Update auth context if available
        if (updateUser) {
          updateUser({
            ...user,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phoneNumber: formData.phoneNumber
          });
        }
      } else {
        setErrorMessage(data.message || data.Message || 'Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrorMessage(error?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({ ...originalData });
    setValidationErrors({});
    setErrorMessage('');
    setIsEditing(false);
  };

  const handlePasswordChangeSuccess = () => {
    setSuccessMessage('Password changed successfully!');
  };

  const fullName = [formData.firstName, formData.lastName].filter(Boolean).join(' ') || 'User';

  // Loading state
  if (loading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow border-0">
              <Card.Body className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <div className="mt-3">Loading profile...</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow border-0">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">Profile Details</h5>
                {!isEditing && (
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </Button>
                )}
              </div>
            </Card.Header>

            <Card.Body className="p-4">
              {/* Avatar Section */}
              <div className="text-center mb-4">
                <div 
                  className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 mb-3"
                  style={{ width: '80px', height: '80px' }}
                >
                  <svg 
                    width="40" 
                    height="40" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-primary"
                  >
                    <path 
                      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h5 className="fw-bold mb-1">{fullName}</h5>
                <small className="text-muted">{formData.email}</small>
              </div>

              {/* Success/Error Messages */}
              {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                  {successMessage}
                </Alert>
              )}
              {errorMessage && (
                <Alert variant="danger" dismissible onClose={() => setErrorMessage('')}>
                  {errorMessage}
                </Alert>
              )}

              {/* Profile Form */}
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        First Name {isEditing && <span className="text-danger">*</span>}
                      </Form.Label>
                      {isEditing ? (
                        <>
                          <Form.Control
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.firstName}
                            disabled={saving}
                          />
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.firstName}
                          </Form.Control.Feedback>
                        </>
                      ) : (
                        <div className="form-control bg-light">{formData.firstName || 'N/A'}</div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Last Name {isEditing && <span className="text-danger">*</span>}
                      </Form.Label>
                      {isEditing ? (
                        <>
                          <Form.Control
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.lastName}
                            disabled={saving}
                          />
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.lastName}
                          </Form.Control.Feedback>
                        </>
                      ) : (
                        <div className="form-control bg-light">{formData.lastName || 'N/A'}</div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Email {isEditing && <span className="text-danger">*</span>}
                  </Form.Label>
                  {isEditing ? (
                    <>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        isInvalid={!!validationErrors.email}
                        disabled={saving}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.email}
                      </Form.Control.Feedback>
                    </>
                  ) : (
                    <div className="form-control bg-light">{formData.email || 'N/A'}</div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="(555) 555-5555"
                      maxLength={14}
                      disabled={saving}
                    />
                  ) : (
                    <div className="form-control bg-light">{formData.phoneNumber || 'Not provided'}</div>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Password</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <div className="form-control bg-light flex-grow-1">••••••••••••</div>
                    <Button
                      variant="outline-warning d-flex align-items-center"
                      onClick={() => setShowPasswordModal(true)}
                      style={{paddingTop:'.4rem!important', paddingBottom:'.4rem!important'}}
                    >
                      <svg 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        className="me-1 "
                      >
                        <path 
                          d="M21 2L19 4M19 4L22 7L18.5 10.5L15.5 7.5M19 4L15.5 7.5M12 12L15.5 7.5M12 12L3 21M12 12L7.5 15.5M3 21L7.5 15.5M3 21L6 24M7.5 15.5L6 24" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                      Change
                    </Button>
                  </div>
                </Form.Group>

                {/* Action Buttons */}
                {isEditing && (
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-grow-1"
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </Form>
            </Card.Body>
          </Card>

          {/* Additional Info */}
          <div className="text-center mt-4">
            <small className="text-muted">
              Need help? Contact your administrator or visit our support page.
            </small>
          </div>
        </Col>
      </Row>

      {/* Change Password Modal */}
      <ChangePasswordModal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </Container>
  );
};

export default ProfilePage;